import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { type AdminRole, AdminRole as AdminRoleValues } from '@/lib/types'

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: AdminRole
}

interface AuthContextValue {
  user: AdminUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isSuperAdmin: boolean
  isAdmin: boolean
  isViewer: boolean
  login: (email: string) => Promise<void>
  verifyOtp: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function fetchMe(): Promise<AdminUser | null> {
  try {
    return await apiClient.get<AdminUser>(endpoints.auth.me)
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchMe()
      .then((data) => {
        if (data) setUser(data)
      })
      .finally(() => setIsLoading(false))
  }, [])

  async function login(email: string): Promise<void> {
    await apiClient.post(endpoints.auth.requestOtp, { email })
  }

  async function verifyOtp(email: string, code: string): Promise<void> {
    await apiClient.post(endpoints.auth.verifyOtp, { email, code })
    const data = await fetchMe()
    if (data) setUser(data)
  }

  async function logout(): Promise<void> {
    await apiClient.post(endpoints.auth.logout)
    setUser(null)
  }

  const isSuperAdmin = user?.role === AdminRoleValues.SUPER_ADMIN
  const isAdmin = isSuperAdmin || user?.role === AdminRoleValues.ADMIN
  const isViewer = user?.role === AdminRoleValues.VIEWER

  return (
    <AuthContext
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isSuperAdmin,
        isAdmin,
        isViewer,
        login,
        verifyOtp,
        logout,
      }}
    >
      {children}
    </AuthContext>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
